/**
 * One client, and everything hanging off them, on one screen.
 *
 * The three things a recruiter needs about a client were in three places: the
 * client's own details on the Clients list, the people who interview nowhere at
 * all, and their requirements on Job postings — a page organised by role rather
 * than by client, where "what is Apollo asking us for" meant filtering a list of
 * everybody's roles.
 *
 * Raising a requirement happens here too, for the same reason. A requirement
 * belongs to a client and is raised in a conversation with that client; sending
 * somebody to another page to record it, with the client as a dropdown they
 * have to find again, is how requirements go unlogged — and an unlogged
 * requirement is what leaves a submission unlinked.
 *
 * Each requirement stays its own record. A client raises many over time and
 * they must not overwrite each other: the second Staff Nurse requirement in
 * March is not a correction of the first.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Loader2,
  Mail,
  Phone,
  Plus,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { monthsFromYears } from '../lib/experience';
import { money } from '../lib/money';
import { formatPhone, telHref } from '../lib/phone';
import { titleCaseName } from '../lib/titleCase';
import type {
  Client as ClientRow,
  ClientAddress,
  EscalationContact,
  GstRegistration,
  InterviewContact,
  JobOpening,
  Page,
} from '../types';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5 text-sm">
      <dt className="w-40 shrink-0 text-gray-500">{label}</dt>
      <dd className="min-w-0 flex-1 text-gray-900">
        {value || <span className="text-gray-300">—</span>}
      </dd>
    </div>
  );
}

/** The sentence a section shows when it has nothing to list. */
function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}

function Section({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          {icon}
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Months as years-and-months, because a requirement is stated that way. */
function experienceBand(min: number | null, max: number | null): string | null {
  if (min === null && max === null) return null;
  const years = (m: number) => {
    const y = Math.floor(m / 12);
    const rest = m % 12;
    return [y ? `${y}y` : '', rest ? `${rest}m` : ''].filter(Boolean).join(' ') || '0m';
  };
  if (min !== null && max !== null) return `${years(min)} – ${years(max)}`;
  if (min !== null) return `${years(min)}+`;
  return `up to ${years(max as number)}`;
}

export default function ClientDetailPage() {
  const { clientId = '' } = useParams();
  const queryClient = useQueryClient();
  /**
   * Open the requirement form on arrival when we were sent here to raise one.
   *
   * Carried in navigation state rather than the URL: it describes how this
   * page was reached, not what it is showing, and a reload should land on the
   * plain client page rather than reopening a form somebody has since closed.
   */
  const location = useLocation();
  const arrivedToRaise = Boolean(
    (location.state as { raiseRequirement?: boolean } | null)?.raiseRequirement,
  );
  const [raising, setRaising] = useState(arrivedToRaise);
  const [addingContact, setAddingContact] = useState(false);

  const clientQuery = useQuery<ClientRow>({
    queryKey: ['client', clientId],
    queryFn: async () => (await client.get(`/api/v1/clients/${clientId}`)).data,
    retry: false,
  });

  const contacts = useQuery<InterviewContact[]>({
    queryKey: ['client', clientId, 'interview-contacts'],
    queryFn: async () =>
      (await client.get(`/api/v1/clients/${clientId}/interview-contacts`)).data,
  });

  // The three lists that used to be flat columns, or did not exist at all.
  // Separate queries rather than one composite payload: each is its own
  // endpoint, each invalidates on its own writes, and a client with none of
  // them costs three empty arrays rather than a wider client row.
  const escalations = useQuery<EscalationContact[]>({
    queryKey: ['client', clientId, 'escalation-contacts'],
    queryFn: async () =>
      (await client.get(`/api/v1/clients/${clientId}/escalation-contacts`)).data,
  });

  const addresses = useQuery<ClientAddress[]>({
    queryKey: ['client', clientId, 'addresses'],
    queryFn: async () =>
      (await client.get(`/api/v1/clients/${clientId}/addresses`)).data,
  });

  const registrations = useQuery<GstRegistration[]>({
    queryKey: ['client', clientId, 'gst-registrations'],
    queryFn: async () =>
      (await client.get(`/api/v1/clients/${clientId}/gst-registrations`)).data,
  });

  /**
   * Their requirements, newest first — including closed ones.
   *
   * A closed requirement is history rather than noise: it says this client has
   * asked for this role before and what came of it, which is exactly what
   * somebody looking at a new one wants to know.
   */
  const openings = useQuery<Page<JobOpening>>({
    queryKey: ['job-openings', 'for-client', clientId],
    queryFn: async () =>
      (
        await client.get('/api/v1/job-openings', {
          // open_only defaults to false, so closed requirements come back too
          // without asking. That is what is wanted here: a closed one is
          // history rather than noise, and it says this client has asked for
          // this role before and what came of it.
          params: { clientId, limit: 200 },
        })
      ).data,
  });

  const addContact = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post(`/api/v1/clients/${clientId}/interview-contacts`, body)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['client', clientId, 'interview-contacts'],
      });
      setAddingContact(false);
      toast.success('Interview contact added');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not add that contact')),
  });

  const removeContact = useMutation({
    mutationFn: async (id: string) =>
      (await client.delete(`/api/v1/clients/${clientId}/interview-contacts/${id}`)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['client', clientId, 'interview-contacts'],
      });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const raise = useMutation({
    // The prompt sheet is not part of the requirement row. It is pulled out
    // here and written once the requirement has an id, because that is the
    // first moment the questions have anything to hang off — the same shape
    // the client form uses for its interview contacts.
    mutationFn: async (body: Record<string, unknown>) => {
      const { questions = [], ...fields } = body as {
        questions?: string[];
      } & Record<string, unknown>;

      const opening = (
        await client.post('/api/v1/job-openings', { ...fields, clientId })
      ).data as JobOpening;

      for (const question of questions) {
        // Sequential, so a failure names the question it failed on rather
        // than arriving as one of twenty simultaneous errors.
        await client.post(`/api/v1/job-openings/${opening.id}/questions`, {
          question,
        });
      }
      return opening;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job-openings'] });
      setRaising(false);
      toast.success('Requirement raised');
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not raise that requirement')),
  });

  if (clientQuery.isLoading) {
    return (
      <div className="flex justify-center py-16 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const c = clientQuery.data;
  if (!c) {
    return (
      <div className="card text-sm text-gray-600">
        That client could not be loaded.{' '}
        <Link to="/clients" className="text-indigo-600 hover:underline">
          Back to clients
        </Link>
      </div>
    );
  }

  const rows = openings.data?.items ?? [];
  const open = rows.filter((r) => r.isOpen);

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Clients
        </Link>
        <h1 className="mt-1 text-lg font-semibold text-gray-900">{c.name}</h1>
        <p className="mt-0.5 text-xs text-gray-500">
          {open.length} open requirement{open.length === 1 ? '' : 's'}
          {rows.length > open.length && ` · ${rows.length - open.length} closed`}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Contact Details">
          <dl className="divide-y divide-gray-100">
            <Row label="Contact Person" value={titleCaseName(c.contactName)} />
            <Row
              label="Mobile"
              value={
                c.contactPhone ? (
                  <a
                    href={`tel:${telHref(c.contactPhone)}`}
                    className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {formatPhone(c.contactPhone)}
                  </a>
                ) : null
              }
            />
            <Row
              label="Email"
              value={
                c.contactEmail ? (
                  <a
                    href={`mailto:${c.contactEmail}`}
                    className="inline-flex items-center gap-1 break-all text-indigo-600 hover:underline"
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    {c.contactEmail}
                  </a>
                ) : null
              }
            />
            <Row label="Website" value={c.website} />
            <Row label="Head Office" value={c.address} />
            <Row
              label="Location"
              value={[c.city, c.state].filter(Boolean).join(', ') || null}
            />
            <Row label="Industry" value={c.industry} />
          </dl>
        </Section>

        {/* Escalation stays its own block, above the panel: who to chase
            when something has gone wrong, as against who to arrange with when
            it is going right. A list now rather than three columns -- it goes
            to a state-wise HR head, and there is more than one. */}
        <Section title="Escalation Points">
          {escalations.data && escalations.data.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {escalations.data.map((person) => (
                <li key={person.id} className="py-2 text-sm">
                  <p className="font-medium text-gray-900">
                    {titleCaseName(person.name)}
                    {person.designation && (
                      <span className="font-normal text-gray-500">
                        {' \u00b7 '}
                        {person.designation}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {[formatPhone(person.phone), person.email, person.location]
                      .filter(Boolean)
                      .join(' \u00b7 ') || 'No way to reach them recorded'}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Nobody recorded to escalate to.</Empty>
          )}
        </Section>
        <Section title="GST Registrations">
          {registrations.data && registrations.data.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {registrations.data.map((reg) => (
                <li key={reg.id} className="py-2 text-sm">
                  <p className="font-medium tabular-nums text-gray-900">
                    {reg.gstin}
                    {reg.isDefault && (
                      <span className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-normal text-indigo-700">
                        default
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {[reg.state, reg.label, reg.address]
                      .filter(Boolean)
                      .join(' \u00b7 ')}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>
              No registration recorded. An invoice for this client shows one
              unnamed GST line rather than a CGST/SGST split.
            </Empty>
          )}
        </Section>

        <Section title="Job Locations / Addresses">
          {addresses.data && addresses.data.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {addresses.data.map((row) => (
                <li key={row.id} className="py-2 text-sm">
                  {row.label && (
                    <p className="font-medium text-gray-900">{row.label}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    {[row.line, row.city, row.state, row.pincode]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No other location recorded.</Empty>
          )}
        </Section>
      </div>

      <Section
        title="Interview Points"
        icon={<Users className="h-4 w-4 text-gray-400" />}
        action={
          !addingContact && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => setAddingContact(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          )
        }
      >
        {contacts.data?.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-gray-200 text-left text-xs text-gray-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Designation</th>
                  <th className="py-2 pr-4 font-medium">Mobile</th>
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Location</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.data.map((p) => (
                  <tr key={p.id}>
                    <td className="py-2 pr-4 font-medium text-gray-900">{p.name}</td>
                    <td className="py-2 pr-4 text-gray-600">{p.designation ?? '—'}</td>
                    <td className="py-2 pr-4 text-gray-600">{formatPhone(p.phone) ?? '—'}</td>
                    <td className="break-all py-2 pr-4 text-gray-600">
                      {p.email ?? '—'}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">{p.location ?? '—'}</td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-600"
                        title={`Remove ${p.name}`}
                        onClick={() => removeContact.mutate(p.id)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !addingContact && (
            <p className="text-sm text-gray-500">
              Nobody recorded yet — add whoever takes the interviews.
            </p>
          )
        )}

        {addingContact && (
          <ContactForm
            saving={addContact.isPending}
            onCancel={() => setAddingContact(false)}
            onSave={(body) => addContact.mutate(body)}
          />
        )}
      </Section>

      <Section
        title="Requirements"
        icon={<Briefcase className="h-4 w-4 text-gray-400" />}
        action={
          !raising && (
            <button
              type="button"
              className="btn-primary text-xs"
              onClick={() => setRaising(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Raise a requirement
            </button>
          )
        }
      >
        {raising && (
          <RequirementForm
            saving={raise.isPending}
            onCancel={() => setRaising(false)}
            onSave={(body) => raise.mutate(body)}
          />
        )}

        {openings.isLoading ? (
          <div className="flex justify-center py-6 text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">
            Nothing raised yet. Until one is, a candidate tracked to this client
            is recorded without a requirement to sit under.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((r) => (
              <li key={r.id} className="py-2.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium text-gray-900">
                    {r.title}
                  </span>
                  <span
                    className={`badge ${
                      r.isOpen
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {r.isOpen ? 'Open' : 'Closed'}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-gray-500">
                  {[
                    r.positionsOpen ? `${r.positionsOpen} needed` : null,
                    r.location,
                    experienceBand(r.minExperienceMonths, r.maxExperienceMonths),
                    r.minQualification,
                    r.salaryMin || r.salaryMax
                      ? `${money(r.salaryMin)} – ${money(r.salaryMax)}`
                      : null,
                    r.openedOn ? `raised ${r.openedOn}` : null,
                    r.targetCloseOn ? `TAT ${r.targetCloseOn}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function ContactForm({
  saving,
  onCancel,
  onSave,
}: {
  saving: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState('');
  const [designation, setDesignation] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');

  return (
    <form
      className="mt-3 rounded-lg border border-gray-200 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          name: name.trim(),
          designation: designation.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          location: location.trim() || null,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="label" htmlFor="ip-name">Name</label>
          <input
            id="ip-name"
            className="input"
            required
            maxLength={160}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="ip-designation">Designation</label>
          <input
            id="ip-designation"
            className="input"
            maxLength={160}
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="ip-phone">Mobile</label>
          <input
            id="ip-phone"
            className="input"
            inputMode="numeric"
            placeholder="10 digits"
            maxLength={20}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="ip-email">Email</label>
          <input
            id="ip-email"
            type="email"
            className="input"
            maxLength={255}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="ip-location">Location</label>
          <input
            id="ip-location"
            className="input"
            maxLength={160}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary text-xs" disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Add contact
        </button>
      </div>
    </form>
  );
}

function RequirementForm({
  saving,
  onCancel,
  onSave,
}: {
  saving: boolean;
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [title, setTitle] = useState('');
  const [positionsOpen, setPositionsOpen] = useState('1');
  const [location, setLocation] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [minQualification, setMinQualification] = useState('');
  /**
   * Years on the form, months in the column.
   *
   * Recruiters and clients talk in years and the database has always stored
   * months, so the conversion happens here at the boundary rather than being
   * pushed onto either. Nothing filters on these -- the requirement's
   * experience band is stored and displayed and read by no query -- so this
   * changes what somebody types and nothing else. Candidate.experience_months
   * is a different column on a different screen and stays in months, because
   * that one *is* filtered.
   */
  const [minExperienceYears, setMinExperienceYears] = useState('');
  const [maxExperienceYears, setMaxExperienceYears] = useState('');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [workTime, setWorkTime] = useState('');
  const [industryType, setIndustryType] = useState('');
  const [functionalArea, setFunctionalArea] = useState('');
  const [skills, setSkills] = useState('');
  const [hiringProcess, setHiringProcess] = useState('');
  const [genderPreference, setGenderPreference] = useState('');
  const [ageLimit, setAgeLimit] = useState('');
  const [targetCloseOn, setTargetCloseOn] = useState('');
  const [description, setDescription] = useState('');
  const [jdRemarks, setJdRemarks] = useState('');
  const [importantNotes, setImportantNotes] = useState('');
  /** Staged until the requirement exists. Order is the order they are typed. */
  const [questions, setQuestions] = useState<string[]>([]);
  const [draftQuestion, setDraftQuestion] = useState('');

  const text = (v: string) => v.trim() || null;
  const num = (v: string) => (v.trim() === '' ? null : Number(v));

  return (
    <form
      className="mb-4 rounded-lg border border-gray-200 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          title: title.trim(),
          positionsOpen: num(positionsOpen) ?? 1,
          location: text(location),
          clientAddress: text(clientAddress),
          minQualification: text(minQualification),
          minExperienceMonths: monthsFromYears(minExperienceYears),
          maxExperienceMonths: monthsFromYears(maxExperienceYears),
          salaryMin: text(salaryMin),
          salaryMax: text(salaryMax),
          workTime: text(workTime),
          industryType: text(industryType),
          functionalArea: text(functionalArea),
          skills: text(skills),
          hiringProcess: text(hiringProcess),
          genderPreference: text(genderPreference),
          ageLimit: text(ageLimit),
          targetCloseOn: text(targetCloseOn),
          description: text(description),
          jdRemarks: text(jdRemarks),
          importantNotes: text(importantNotes),
          // Not a field on the requirement — see the mutation, which writes
          // these once it has an id.
          questions,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="label" htmlFor="rq-title">Job Title</label>
          <input
            id="rq-title"
            className="input"
            required
            maxLength={160}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-count">Headcount</label>
          <input
            id="rq-count"
            type="number"
            min={1}
            className="input"
            value={positionsOpen}
            onChange={(e) => setPositionsOpen(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-location">Work Location</label>
          <input
            id="rq-location"
            className="input"
            maxLength={120}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-address">Client Address</label>
          <input
            id="rq-address"
            className="input"
            maxLength={255}
            value={clientAddress}
            onChange={(e) => setClientAddress(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-qual">Min. Education/Qualification</label>
          <input
            id="rq-qual"
            className="input"
            maxLength={160}
            value={minQualification}
            onChange={(e) => setMinQualification(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-expmin">Exp. Min (Years)</label>
          <input
            id="rq-expmin"
            type="number"
            min={0}
            max={60}
            step={0.5}
            className="input"
            value={minExperienceYears}
            onChange={(e) => setMinExperienceYears(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-expmax">Exp. Max (Years)</label>
          <input
            id="rq-expmax"
            type="number"
            min={0}
            max={60}
            step={0.5}
            className="input"
            value={maxExperienceYears}
            onChange={(e) => setMaxExperienceYears(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-time">Job Timings</label>
          <input
            id="rq-time"
            className="input"
            maxLength={120}
            placeholder="e.g. 9-6, rotational"
            value={workTime}
            onChange={(e) => setWorkTime(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-salmin">Salary Range From</label>
          <input
            id="rq-salmin"
            className="input"
            inputMode="decimal"
            value={salaryMin}
            onChange={(e) => setSalaryMin(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-salmax">Salary Range To</label>
          <input
            id="rq-salmax"
            className="input"
            inputMode="decimal"
            value={salaryMax}
            onChange={(e) => setSalaryMax(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-tat">Requirements Closing Date</label>
          <input
            id="rq-tat"
            type="date"
            className="input"
            value={targetCloseOn}
            onChange={(e) => setTargetCloseOn(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-industry">Industry</label>
          <input
            id="rq-industry"
            className="input"
            maxLength={120}
            value={industryType}
            onChange={(e) => setIndustryType(e.target.value)}
          />
        </div>
        {/* Functional area is what the workbook calls Job Function — one
            field, not two names for the same thing. */}
        <div>
          <label className="label" htmlFor="rq-function">
            Functional Area
          </label>
          <input
            id="rq-function"
            className="input"
            maxLength={120}
            value={functionalArea}
            onChange={(e) => setFunctionalArea(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="label" htmlFor="rq-skills">Required 5 Imp Skills</label>
          <input
            id="rq-skills"
            className="input"
            placeholder="The skills needed for this job"
            value={skills}
            onChange={(e) => setSkills(e.target.value)}
          />
        </div>
      </div>

      {/* What the client stated about who they will consider. Free text, and
          nothing reads it to filter or rank candidates — an age limit that
          became two numbers would end up compared against a date of birth, and
          a screen that silently drops people for their age leaves no trace of
          the decision. A recruiter reading the line does. */}
      <fieldset className="mt-3 rounded border border-amber-200 bg-amber-50 p-2.5">
        <legend className="px-1 text-xs font-medium text-amber-900">
          Stated By the Client
        </legend>
        <p className="mb-2 text-[11px] text-amber-800">
          Recorded as the client put it. Nothing filters or excludes candidates
          on these — they are a note for whoever is shortlisting.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="rq-gender">Gender</label>
            <input
              id="rq-gender"
              className="input"
              maxLength={120}
              value={genderPreference}
              onChange={(e) => setGenderPreference(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="rq-age">Age Limit</label>
            <input
              id="rq-age"
              className="input"
              maxLength={120}
              value={ageLimit}
              onChange={(e) => setAgeLimit(e.target.value)}
            />
          </div>
        </div>
      </fieldset>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="rq-process">
            Interview Mode/Hiring Process
          </label>
          <textarea
            id="rq-process"
            className="input"
            rows={2}
            value={hiringProcess}
            onChange={(e) => setHiringProcess(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="rq-roles">
            Roles &amp; Responsibilities
          </label>
          <textarea
            id="rq-roles"
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="rq-remarks">
            The Complete Job Description
          </label>
          <textarea
            id="rq-remarks"
            className="input"
            rows={2}
            value={jdRemarks}
            onChange={(e) => setJdRemarks(e.target.value)}
          />
        </div>

        <div className="sm:col-span-2">
          <label className="label" htmlFor="rq-notes">
            Important Notes
          </label>
          <textarea
            id="rq-notes"
            className="input"
            rows={2}
            value={importantNotes}
            onChange={(e) => setImportantNotes(e.target.value)}
            placeholder="Ongoing issues and selection tips for this requirement"
          />
          <p className="mt-1 text-xs text-gray-500">
            What the desk learns while filling it — that the panel rejects
            anyone without a two-wheeler, that Tuesday slots never hold. Added
            to later; the description above is taken down once.
          </p>
        </div>
      </div>

      {/* The prompt sheet.
          Staged here and written once the requirement has an id, so a
          question typed before Save is not lost to a validation error on a
          field three rows up. Order is the order they are typed; there is
          deliberately no reordering, because a half-done version of it leaves
          two questions claiming the same place. */}
      <div className="mt-4 rounded-lg border border-gray-200 p-3">
        <p className="text-xs font-medium text-gray-700">
          Questions for the Candidate
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          A sheet the recruiter reads down on the call. Nothing records an
          answer.
        </p>

        {questions.length > 0 && (
          <ol className="mt-2 space-y-1">
            {questions.map((q, index) => (
              <li
                key={`${index}-${q}`}
                className="flex items-start justify-between gap-2 rounded-md bg-gray-50 px-2 py-1.5 text-xs"
              >
                <span className="min-w-0">
                  <span className="mr-1.5 tabular-nums text-gray-400">
                    {index + 1}.
                  </span>
                  <span className="text-gray-900">{q}</span>
                </span>
                <button
                  type="button"
                  className="btn-ghost shrink-0 px-1.5 py-0.5 text-xs text-red-600"
                  onClick={() =>
                    setQuestions(questions.filter((_, i) => i !== index))
                  }
                  aria-label={`Remove question ${index + 1}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ol>
        )}

        <div className="mt-2 flex gap-2">
          <input
            className="input text-xs"
            value={draftQuestion}
            maxLength={1000}
            placeholder="e.g. Do you have your own two-wheeler?"
            onChange={(e) => setDraftQuestion(e.target.value)}
            // Enter adds the question rather than submitting the requirement,
            // which is what a text input inside a form does by default and
            // would raise the requirement half-typed.
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const text = draftQuestion.trim();
              if (!text) return;
              setQuestions([...questions, text]);
              setDraftQuestion('');
            }}
          />
          <button
            type="button"
            className="btn-secondary shrink-0 text-xs"
            disabled={!draftQuestion.trim()}
            onClick={() => {
              setQuestions([...questions, draftQuestion.trim()]);
              setDraftQuestion('');
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add question
          </button>
        </div>
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary text-xs" disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Raise requirement
        </button>
      </div>
    </form>
  );
}
